import api from './apiconfig.js';
/**
 * Order API Service
 * Handles order creation and management operations
 */

const API_BASE_URL = api.API_BASE; // Update with your backend URL

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Network error' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.json();
};

export const createWalkinOrder = async (orderData) => {
  const response = await fetch(`${API_BASE_URL}/orders/walkin`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(orderData)
  });
  return handleResponse(response);
};

export const createTableOrder = async (orderData) => {
  const response = await fetch(`${API_BASE_URL}/orders/table`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(orderData)
  });
  return handleResponse(response);
};

export const createWaiterOrder = async (orderData) => {
  const response = await fetch(`${API_BASE_URL}/orders/waiter`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(orderData)
  });
  return handleResponse(response);
};

export const createOrder = async (orderData) => {
  if (orderData.order_type === 'walkin') {
    return createWalkinOrder(orderData);
  }
  if (orderData.order_type === 'table') {
    return createTableOrder(orderData);
  }
  if (orderData.order_type === 'waiter') {
    return createWaiterOrder(orderData);
  }

  const response = await fetch(`${API_BASE_URL}/orders`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(orderData)
  });
  return handleResponse(response);
};

export const getOrders = async ({ start, end, businessDate, orderType, status } = {}) => {
  const params = new URLSearchParams();
  if (typeof start === 'number' && !Number.isNaN(start)) {
    params.append('start', start);
  }
  if (typeof end === 'number' && !Number.isNaN(end)) {
    params.append('end', end);
  }
  if (businessDate) {
    params.append('business_date', businessDate);
  }
  if (orderType) {
    params.append('order_type', orderType);
  }
  if (status) {
    params.append('status', status);
  }

  const url = `${API_BASE_URL}/orders${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url, {
    headers: getAuthHeaders()
  });
  const data = await handleResponse(response);
  return data;
};

export const getAdminUserOrderHistory = async () => {
  const response = await fetch(`${API_BASE_URL}/orders/user-history`, {
    headers: getAuthHeaders()
  });
  return handleResponse(response);
};

export const getAdminWaiterPerformance = async () => {
  const response = await fetch(`${API_BASE_URL}/orders/waiter-performance`, {
    headers: getAuthHeaders()
  });
  return handleResponse(response);
};

export const getOrderById = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}`, {
    headers: getAuthHeaders()
  });
  const data = await handleResponse(response);
  return data;
};

export const createReceipt = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}/receipt`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  const data = await handleResponse(response);
  return data;
};

export const createToken = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}/token`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  const data = await handleResponse(response);
  return data;
};

export const appendOrderItems = async (orderId, items, action = 'save') => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}/items`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ items, action })
  });
  const data = await handleResponse(response);
  return data;
};

export const updateOrderItems = async (orderId, items) => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}/update`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ items, action: 'update' })
  });
  const data = await handleResponse(response);
  return data;
};

export const getReceipt = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}/receipt`, {
    headers: getAuthHeaders()
  });
  const data = await handleResponse(response);
  return data;
};

export const getToken = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}/token`, {
    headers: getAuthHeaders()
  });
  const data = await handleResponse(response);
  return data;
};

export const getPendingTokens = async () => {
  const response = await fetch(`${API_BASE_URL}/orders/tokens/pending`, {
    headers: getAuthHeaders()
  });
  const data = await handleResponse(response);
  return data;
};

export const cancelOrder = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}/cancel`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({})
  });
  const data = await handleResponse(response);
  return data;
};

export const completeOrder = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/orders/${orderId}/complete`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({})
  });
  const data = await handleResponse(response);
  return data;
};
